/**
 * Pagare — Comprobante de pago parcial + pagaré del saldo restante, para imprimir.
 *
 * Uso:
 *   <Pagare data={pagareData} onClose={() => setData(null)} />
 *
 * El overlay es invisible en pantalla pero al imprimir sólo se imprime este
 * componente (reutiliza las clases conformidad-* — ver CSS @media print en index.css).
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Printer } from "lucide-react";
import { Button } from "@/components/ui";
import { API_BASE } from "@/lib/api";

function fmtG(val) {
  const n = Number(val || 0);
  return "₲ " + n.toLocaleString("es-PY", { maximumFractionDigits: 0 });
}

function fmtFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function Pagare({ data, onClose }) {
  const portalEl = useRef(null);
  if (!portalEl.current) {
    portalEl.current = document.createElement("div");
    portalEl.current.className = "conformidad-portal";
  }

  useEffect(() => {
    document.body.appendChild(portalEl.current);
    return () => {
      if (portalEl.current && document.body.contains(portalEl.current)) {
        document.body.removeChild(portalEl.current);
      }
    };
  }, []);

  if (!data) return null;

  const emp = data.empresa || {};
  const vehiculoDesc = [data.vehiculo_marca, data.vehiculo_modelo].filter(Boolean).join(" ") || "—";
  const chequeInicial = (data.cheques ?? []).find((c) => c.concepto === "INICIAL");

  function handlePrint() {
    const prev = document.title;
    document.title = `Pagare - ${data.numero || ""}`;
    window.print();
    setTimeout(() => { document.title = prev; }, 500);
  }

  const content = (
    <>
      <div className="conformidad-screen fixed inset-0 z-50 flex items-start justify-center
                   bg-black/60 overflow-y-auto py-8 px-4">
        <div className="no-print flex gap-2 fixed top-4 right-4 z-60">
          <Button size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button size="sm" variant="outline" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div
          className="conformidad-sheet bg-white text-black w-[210mm] min-h-[297mm]
                     shadow-2xl p-[18mm] font-serif text-[11pt] leading-relaxed"
          style={{ fontFamily: "'Times New Roman', Times, serif" }}
        >
          <img
            src="/watermark.png"
            alt=""
            aria-hidden="true"
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", width: "55%", maxWidth: "420px",
              opacity: 0.11, pointerEvents: "none", zIndex: 0,
            }}
          />

          {/* Encabezado */}
          <div className="flex items-start justify-between border-b-2 border-black pb-3 mb-4">
            <div className="flex flex-col items-start shrink-0">
              {emp.logo_url ? (
                <img
                  src={`${API_BASE.replace(/\/api\/?$/, "")}${emp.logo_url}`}
                  alt="Logo"
                  style={{ maxWidth: "90px", maxHeight: "70px", objectFit: "contain" }}
                />
              ) : (
                <div className="border border-black px-3 py-1 text-center leading-tight">
                  <div className="font-bold text-[10pt] tracking-widest">VISION</div>
                  <div className="font-bold text-[10pt] tracking-widest">MOTORS</div>
                </div>
              )}
            </div>
            <div className="flex-1 text-center px-4">
              <p className="font-bold text-[13pt]">{emp.nombre || "VISION MOTORS"}</p>
              <p className="text-[9pt]">{emp.label || "Taller de reparación de automóviles - Estética Automotriz"}</p>
              <p className="text-[9pt]">{emp.direccion || "Av. Bicentenario y Sucre, Km 5/2"}</p>
            </div>
            <div className="w-24 text-right text-[9pt] shrink-0">
              <p className="font-semibold">N° {data.numero}</p>
              <p>{fmtFecha(data.fecha)}</p>
            </div>
          </div>

          <div className="text-center my-4">
            <p className="font-bold underline text-[12pt] tracking-wide">
              PAGARÉ — SALDO PENDIENTE
            </p>
          </div>

          {/* Datos principales */}
          <table className="w-full border-collapse text-[10pt] mb-4">
            <tbody>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold w-[20%]">Cliente:</td>
                <td className="border border-black px-2 py-1" colSpan={3}>{data.cliente_nombre || "—"}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Teléfono:</td>
                <td className="border border-black px-2 py-1 w-[30%]">{data.cliente_telefono || "—"}</td>
                <td className="border border-black px-2 py-1 font-semibold w-[20%]">CI/RUC:</td>
                <td className="border border-black px-2 py-1">{data.cliente_ci || "—"}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Vehículo:</td>
                <td className="border border-black px-2 py-1">{vehiculoDesc}</td>
                <td className="border border-black px-2 py-1 font-semibold">Chapa:</td>
                <td className="border border-black px-2 py-1">{data.vehiculo_patente || "—"}</td>
              </tr>
            </tbody>
          </table>

          {/* Trabajo realizado */}
          <p className="font-bold underline text-[10.5pt] mb-1">TRABAJO REALIZADO</p>
          <table className="w-full border-collapse text-[10pt] mb-4">
            <thead>
              <tr>
                <th className="border border-black px-2 py-1 text-left">Descripción</th>
                <th className="border border-black px-2 py-1 text-right w-[15%]">Cant.</th>
                <th className="border border-black px-2 py-1 text-right w-[22%]">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.items ?? []).map((i) => (
                <tr key={i.id}>
                  <td className="border border-black px-2 py-1">{i.descripcion}</td>
                  <td className="border border-black px-2 py-1 text-right">{i.cantidad}</td>
                  <td className="border border-black px-2 py-1 text-right">{fmtG(i.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Montos */}
          <table className="w-full border-collapse text-[10.5pt] mb-4">
            <tbody>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold w-[60%]">Total del trabajo</td>
                <td className="border border-black px-2 py-1 text-right font-mono">{fmtG(data.total_general)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Pago inicial abonado ({data.forma_pago})</td>
                <td className="border border-black px-2 py-1 text-right font-mono">{fmtG(data.monto_pagado)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-bold">SALDO PENDIENTE</td>
                <td className="border border-black px-2 py-1 text-right font-mono font-bold text-[12pt]">{fmtG(data.saldo_pendiente)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Fecha límite de pago del saldo</td>
                <td className="border border-black px-2 py-1 text-right">{fmtFecha(data.fecha_vencimiento_saldo)}</td>
              </tr>
            </tbody>
          </table>

          {chequeInicial && (
            <table className="w-full border-collapse text-[10pt] mb-4">
              <tbody>
                <tr>
                  <td className="border border-black px-2 py-1 font-semibold w-[30%]" colSpan={4}>
                    DATOS DEL CHEQUE RECIBIDO (abono inicial)
                  </td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1 font-semibold w-[20%]">Banco:</td>
                  <td className="border border-black px-2 py-1 w-[30%]">{chequeInicial.banco || "—"}</td>
                  <td className="border border-black px-2 py-1 font-semibold w-[20%]">N° Cheque:</td>
                  <td className="border border-black px-2 py-1">{chequeInicial.numero_cheque || "—"}</td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1 font-semibold">Emitente:</td>
                  <td className="border border-black px-2 py-1">{chequeInicial.emitente || "—"}</td>
                  <td className="border border-black px-2 py-1 font-semibold">Vence:</td>
                  <td className="border border-black px-2 py-1">{fmtFecha(chequeInicial.fecha_vencimiento)}</td>
                </tr>
              </tbody>
            </table>
          )}

          <div className="my-5 text-[10.5pt] leading-loose">
            <p>
              Por el presente documento, el cliente arriba mencionado reconoce adeudar al TALLER{" "}
              <strong>VISION MOTORS</strong> la suma de <strong>{fmtG(data.saldo_pendiente)}</strong> en
              concepto de saldo restante por el trabajo detallado, comprometiéndose a cancelarlo en su
              totalidad a más tardar el <strong>{fmtFecha(data.fecha_vencimiento_saldo)}</strong>.
            </p>
          </div>

          {/* Firma */}
          <div className="mt-10 space-y-5 text-[10.5pt]">
            {["FIRMA", "ACLARACION", "N° DE CEDULA", "NRO DE CONTACTO"].map((label) => (
              <div key={label} className="flex items-end gap-2">
                <span className="font-bold whitespace-nowrap w-44">{label}</span>
                <span className="flex-1 border-b border-black" style={{ minWidth: "200px" }} />
              </div>
            ))}
          </div>

          <div className="mt-12 text-center text-[9pt] border-t border-black pt-3">
            <p>{emp.direccion || "Av. Bicentenario c/sucre km 5/2 barrio – cuidad del este – py"}</p>
            <p>Tel:{emp.telefono || "0973-779-893"}</p>
            <p>Correo: {emp.email || "tdeste2024@gmail.com"}</p>
            <p>Ruc: {emp.ruc || "80145497-2"}</p>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, portalEl.current);
}
